import React from 'react';
import { Modal, Loading } from '@brighterion/br-ui';
import { Trans, useTranslation } from 'react-i18next';
import { Status } from 'Common/parameterEnums';
import usePatchExpressionParameter from 'Hooks/query-hooks/parameters/usePatchExpressionParameter';
import useDeleteParameter from 'Hooks/query-hooks/parameters/useDeleteParameter';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import styles from './DeleteParamModal.css';

type Props = {
    onClose: () => void;
    paramToDelete: ParameterGETResponse;
};

const DeleteParamModal = ({ onClose, paramToDelete }: Props) => {
    const { id, status, message } = paramToDelete;
    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const { addModal } = useReduxModal();

    const onSuccess = () => {
        addAlert({
            id: 'delete-parameter-success',
            content: (
                <Trans i18nKey='deleteParameter.successAlert'>
                    Parameter <strong>{{ paramName: message }}</strong> successfully deleted.
                </Trans>
            ),
            status: 'success'
        });
        onClose();
    };
    const onError = () => {
        addModal({
            id: 'delete-parameter-error-modal',
            title: t('deleteParameter.genericErrorTitle'),
            status: 'error',
            content: (
                <Trans i18nKey='deleteParameter.genericError'>
                    Parameter <strong>{{ paramName: message }}</strong> could not be deleted. Please try again.
                </Trans>
            ),
            cancelText: t('Close')
        });
        onClose();
    };
    const mutationOptions = { onSuccess, onError };
    const patchMutate = usePatchExpressionParameter({ mutationOptions });
    const deleteMutate = useDeleteParameter({ mutationOptions });

    const isLoading = deleteMutate.isLoading || patchMutate.isLoading;

    const onDelete = () => {
        // Non-active parameters can be deleted directly
        if (status !== Status.ACTIVE) {
            const idsToDelete = [id];
            deleteMutate.mutate({
                idsToDelete
            });
        } else {
            const payload: PatchParameterRequest = {
                id,
                status: Status.READY_TO_DELETE
            };

            patchMutate.mutate(payload);
        }

        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                target: 'parameters',
                operation: 'Delete Parameter',
                clickIdentifier: 'Parameters - Delete Parameter',
                text: 'Delete Parameter'
            }
        });
    };

    const modalBody = (
        <div data-testid='delete-param-with-name' className={styles.sectionOne}>
            <Trans i18nKey='deleteParameter.areYouSure'>
                Are you sure you want to delete the parameter <strong>{{ paramName: message }}</strong>? This action
                cannot be undone.
            </Trans>

            {/* if deleting deployed parameter */}
            {status === Status.ACTIVE && (
                <div data-testid='delete-active-param-info'>
                    <br />
                    <div>{t('deleteParameter.nextDeployment')}</div>
                </div>
            )}
        </div>
    );

    return (
        <Modal
            dataTestId='delete-param-modal'
            isOpen={paramToDelete !== undefined}
            closeModal={onClose}
            status='warning'
            containerClass={styles.modalContainer}
            closeOnClickOutside={false}
            isSubmitting={isLoading}
        >
            <Modal.Title
                id='delete-param-title'
                title={t('deleteParameter.deleteParameter')}
                statusIcon='warning'
                onClose={onClose}
                showCloseIcon={!isLoading}
            />
            <div className={styles.deleteWarningContent}>{modalBody}</div>
            <Modal.Footer
                id='delete-param-footer'
                isSubmitting={isLoading}
                onAction={onDelete}
                onActionText={isLoading ? <Loading size='sm' fillName='white' /> : t('Delete')}
                onClose={onClose}
                onCloseText={t('Cancel')}
            />
        </Modal>
    );
};

export default DeleteParamModal;
